import React, { useMemo, useState } from "react";
import {
  CalendarDays,
  Download,
  RefreshCcw,
  Search,
  Edit3,
  X,
  AlertTriangle,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import useSettlement from "../../Services/OrderServices/SettlementServices/useSettlement";
import useOrders from "../../Services/OrderServices/useOrders";
import { decodeEmojiText } from "../../../Utils/utils";

const PAGE_SIZE = 25;

export default function ReconReviseMapReport() {
  const { getOrderDetails } = useOrders();

  const { getRestaurantWiseTransactions, updateSettlementTransaction } =
    useSettlement();

  const today = () => new Date().toISOString().slice(0, 10);

  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const fetchReviseMap = async () => {
    if (!date) {
      toast.error("Please select date");
      return;
    }

    setLoading(true);
    try {
      const result = await getRestaurantWiseTransactions({
        userid: "0",
        date,
      });

      if (result.success) {
        setRows(Array.isArray(result.data) ? result.data : []);
        setPage(1);
        setSearched(true);
        toast.success(result.message || "Data fetched successfully");
      } else {
        setRows([]);
        setSearched(true);
        toast.error(result.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("fetchReviseMap error:", error);
      setRows([]);
      setSearched(true);
      toast.error("Failed to fetch revise map data");
    } finally {
      setLoading(false);
    }
  };

  const openUpdateModal = (row) => {
    setSelectedRow(row);
    setConfirmOpen(true);
  };

  const closeUpdateModal = () => {
    if (updateLoading) return;
    setConfirmOpen(false);
    setSelectedRow(null);
  };

  const handleUpdateSettlement = async () => {
    if (!selectedRow) return;

    setUpdateLoading(true);
    try {
      const result = await updateSettlementTransaction({
        userid: selectedRow?.fk_user_id ?? "",
        orderid: selectedRow?.order_id ?? "",
        pk_orderid: selectedRow?.pk_order_id ?? "",
        ordertype: selectedRow?.order_type ?? "",
      });

      if (result.success) {
        toast.success(result.message || "Updated successfully");
        closeUpdateModal();
        await fetchReviseMap();
      } else {
        toast.error(result.message || "Update failed");
      }
    } catch (e) {
      console.error("handleUpdateSettlement error:", e);
      toast.error("Failed to update settlement transaction");
    } finally {
      setUpdateLoading(false);
    }
  };

  const exportExcel = () => {
    if (!rows.length) {
      toast.error("No data to export");
      return;
    }

    try {
      const exportRows = rows.map((r, i) => ({
        sn: i + 1,
        pk_order_id: r?.pk_order_id || "",
        us_name: r?.us_name,
        order_date: r?.order_date,
        fk_user_id: r?.fk_user_id || "",
        order_id: r?.order_id || "",
        daily_order_number: r?.daily_order_number || "",
        order_type: r?.order_type || "",
        subtotal: r?.subtotal || "0",
        order_amount: r?.order_amount || "0",
        pay_amount: r?.pay_amount || "0",
        customer_mobile: r?.customer_mobile || "",
        customer_name: r?.customer_name || "",
        status: r?.status || "",
        payment_status: r?.payment_status || "",
        tax_sgst: r?.tax_sgst || "0",
        tax_cgst: r?.tax_cgst || "0",
        payment_link: r?.payment_link || "",
        taxOnCommission: r?.taxOnCommission || "0",
        chaafoCommission: r?.chaafoCommission || "0",
        settelment_value: r?.settelment_value || "0",
        delivery_fee: r?.delivery_fee || "0",
      }));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ReviseMap");

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });

      saveAs(
        new Blob([excelBuffer], { type: "application/octet-stream" }),
        `ReviseMap_${date}.xlsx`,
      );

      toast.success("Excel exported");
    } catch (e) {
      console.error("exportExcel error:", e);
      toast.error("Export failed");
    }
  };

  const money = (v) =>
    v == null || isNaN(Number(v)) ? "₹ 0.00" : `₹ ${Number(v).toFixed(2)}`;

  const filteredRows = useMemo(() => {
    if (!search) return rows;

    const s = search.toLowerCase();

    return rows.filter(
      (r) =>
        (r?.order_id || "").toLowerCase().includes(s) ||
        (r?.customer_name || "").toLowerCase().includes(s) ||
        (r?.customer_mobile || "").toLowerCase().includes(s) ||
        (r?.pk_order_id || "").toLowerCase().includes(s) ||
        (r?.fk_user_id || "").toLowerCase().includes(s),
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  const handleOrderClick = async (orderId) => {
    if (!orderId) return;

    try {
      setDetailsOpen(true);
      setDetailsLoading(true);

      const data = await getOrderDetails(orderId);

      setDetails(data || {});
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch order");
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Revise Map</h2>
          <p className="text-sm text-gray-500">
            Fetch restaurant-wise transactions and update settlement mapping
          </p>
        </div>

        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
          onClick={fetchReviseMap}
          disabled={loading}
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <CalendarDays size={16} />
          Filters
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <input
              type="text"
              placeholder="Order ID, Customer, Mobile..."
              className="h-10 w-64 rounded-xl border px-3 text-sm outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Date</div>
            <input
              type="date"
              className="h-10 w-52 rounded-xl border px-3 text-sm outline-none"
              value={date}
              max={today()}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>

          <div className="ml-auto flex gap-2">
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={exportExcel}
              disabled={!rows.length || loading}
            >
              <Download size={16} />
              Export Excel
            </button>

            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#705CFE] hover:bg-[#5B47F5] px-4 text-sm font-semibold text-white  disabled:opacity-60"
              onClick={fetchReviseMap}
              disabled={loading}
            >
              <Search size={16} />
              {loading ? "Fetching..." : "Fetch"}
            </button>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          Records: <b className="text-gray-700">{filteredRows.length}</b>
        </div>
      </div>

      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#705CFE] text-white">
              <tr>
                {[

                  "Order ID",
                  "Restaurant",
                  "Subtotal",
                  "Order Amount",
                  "Pay Amount",
                  "Customer",
                  "Payment",
                  "Tax GST",
                  "Tsx Com.",
                  "Commission",
                  "Settlement Value",
                  "Delivery Fee",
                ].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap px-4 py-2 font-semibold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!searched && !loading && (
                <tr>
                  <td
                    colSpan={15}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Select date and click <b>Fetch</b>.
                  </td>
                </tr>
              )}

              {searched && !loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={15}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No records found for selected date.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td
                    colSpan={15}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              )}

              {!loading &&
                paginatedRows.map((r, idx) => (
                  <tr key={`${r?.pk_order_id}-${idx}`} className="border-t">
                   


                    <td className="px-4 py-2 max-w-[160px]">
                      <div className="flex flex-col leading-tight">

                        {/* Order ID (clickable) */}
                        <a
                          onClick={() => handleOrderClick(r?.order_id)}
                          className="font-semibold text-blue-700 hover:text-blue-900 underline truncate whitespace-nowrap cursor-pointer"
                          title={`#${r?.order_id}`}
                        >
                          #{r?.order_id ?? "N/A"}
                        </a>

                        {/* Order Type */}
                        <span
                          className="text-xs text-gray-500 capitalize truncate whitespace-nowrap"
                          title={r?.order_type}
                        >
                          {r?.order_type?.replace("_", " ") ?? "N/A"}
                        </span>

                      </div>
                    </td>
                    <td className="px-4 py-2 max-w-[180px]">
                      <div className="flex flex-col leading-tight">

                        {/* Name */}
                        <span
                          className="font-semibold text-gray-800 text-sm truncate whitespace-nowrap"
                          title={r?.us_name}
                        >
                          {r?.us_name ?? "N/A"}
                        </span>

                        {/* Date */}
                        <span
                          className="text-xs text-gray-500 truncate whitespace-nowrap"
                          title={r?.order_date}
                        >
                          {r?.order_date ?? "N/A"}
                        </span>

                      </div>
                    </td>
                    {/* Amounts */}
                    <td className="px-4 py-2">{money(r?.subtotal)}</td>
                    <td className="px-4 py-2">{money(r?.order_amount)}</td>
                    <td className="px-4 py-2 font-semibold">
                      {money(r?.pay_amount)}
                    </td>

                    {/* Customer */}
                    <td className="px-4 py-2">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-800">
                          {decodeEmojiText(r?.customer_name || "N/A")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {r?.customer_mobile ?? "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Payment Status */}
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                        ✓
                      </span>
                    </td>

                    {/* Taxes */}
                    <td className="px-4 py-2">
                      <div className="flex flex-col">
                      <span>{money(r?.tax_sgst)}</span>
                      <span>
                        {money(r?.tax_cgst)}
                      </span>
                      </div></td>
                    
                    <td className="px-4 py-2">{money(r?.taxOnCommission)}</td>

                    {/* Commission */}
                    <td className="px-4 py-2">{money(r?.chaafoCommission)}</td>

                    {/* Settlement */}
                    <td className="px-4 py-2 font-semibold text-[#705CFE]">
                      {money(r?.settelment_value)}
                    </td>

                    {/* Delivery */}
                    <td className="px-4 py-2">{money(r?.delivery_fee)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {rows.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 border-t p-3">
            <button
              className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {confirmOpen && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-xl bg-amber-100 p-2 text-amber-600">
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <h3 className="text-base font-semibold">Confirm Update</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This will update settlement transaction for this order.
                  </p>
                </div>
              </div>

              <button
                onClick={closeUpdateModal}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                disabled={updateLoading}
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-2 p-4 text-sm">
              <div>
                <b>PK Order ID:</b> {selectedRow?.pk_order_id ?? "N/A"}
              </div>
              <div>
                <b>User ID:</b> {selectedRow?.fk_user_id ?? "N/A"}
              </div>
              <div>
                <b>Order ID:</b> {selectedRow?.order_id ?? "N/A"}
              </div>
              <div>
                <b>Order Type:</b> {selectedRow?.order_type ?? "N/A"}
              </div>
              <div>
                <b>Customer:</b> {selectedRow?.customer_name ?? "N/A"}
              </div>
              <div>
                <b>Settlement Value:</b> {money(selectedRow?.settelment_value)}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t p-4">
              <button
                onClick={closeUpdateModal}
                disabled={updateLoading}
                className="rounded-xl border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateSettlement}
                disabled={updateLoading}
                className="rounded-xl bg-[#01C37D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#00a96b] disabled:opacity-50"
              >
                {updateLoading ? "Updating..." : "Confirm Update"}
              </button>
            </div>
          </div>
        </div>
      )}
      {detailsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white w-[900px] rounded-xl p-4 shadow-xl max-h-[85vh] overflow-auto">
            {/* HEADER */}
            <div className="flex justify-between mb-3 border-b pb-2">
              <div>
                <div>
                  <h3>{details.us_name || "N/A"}</h3>
                </div>
                <h3 className="font-semibold text-lg">
                  Order #{details?.orderid}
                </h3>

                <div className="flex gap-2 mt-1 text-xs">
                  <span className="border px-2 py-1 rounded bg-gray-100">
                    {details?.status?.replaceAll("_", " ")}
                  </span>

                  <span className="border px-2 py-1 rounded bg-gray-100">
                    {details?.orderType?.replaceAll("_", " ")}
                  </span>

                  <span className="border px-2 py-1 rounded bg-green-100 text-green-700">
                    Paid ({details?.payment_mode})
                  </span>

                  <span className="border px-2 py-1 rounded bg-gray-100">
                    {details?.delivery_partner_status}
                  </span>
                </div>

                <div className="text-xs text-gray-500 mt-1">
                  Order: {details?.order_date}
                </div>
              </div>

              <button
                onClick={() => setDetailsOpen(false)}
                className="border px-3 py-1 rounded"
              >
                Close
              </button>
            </div>

            {detailsLoading ? (
              <div className="text-center py-6">Loading order...</div>
            ) : (
              // BODY
              <div className="space-y-3 text-sm">
                {/* SUMMARY + CUSTOMER */}
                <div className="grid grid-cols-2 gap-3">
                  {/* SUMMARY */}
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold mb-2">Summary</h4>

                    <div>
                      <b>Total :</b> ₹{details?.pay_amount}
                    </div>

                    <div>
                      <b>Subtotal :</b> ₹{details?.subtotal}
                    </div>

                    <div>
                      <b>Platform :</b> {details?.platform}
                    </div>

                    <div>
                      <b>Virtual Acc :</b> {details?.virtual_acc}
                    </div>

                    <div>
                      <b>GST Claim :</b> {details?.gst_claim}
                    </div>

                    <div>
                      <b>Loyalty Points :</b> {details?.loyalty_points}
                    </div>
                  </div>

                  {/* CUSTOMER */}
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold mb-2">Customer & Address</h4>

                    <div>
                      <b>Name :</b> {details?.customer_name || "N/A"}
                    </div>

                    <div>
                      <b>Phone :</b> {details?.customer_mobile}
                    </div>

                    <div>
                      <b>Table :</b> {details?.table_no || "N/A"}
                    </div>
                  </div>
                </div>

                {/* ITEMS TABLE */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">#</th>

                        <th className="p-2 text-left">Product</th>

                        <th className="p-2 text-left">Qty</th>

                        <th className="p-2 text-left">Item Price</th>

                        <th className="p-2 text-left">Total</th>

                        <th className="p-2 text-left">Customization</th>
                      </tr>
                    </thead>

                    <tbody>
                      {details?.items?.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-2">{index + 1}</td>

                          <td className="p-2">{item?.item_name}</td>

                          <td className="p-2">{item?.quantity}</td>

                          <td className="p-2">₹{item?.item_price}</td>

                          <td className="p-2">₹{item?.total_price}</td>

                          <td className="p-2">{item?.customization || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
